import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import type { Budget, ExpenseCategory, I18n } from '../types'

/**
 * Le budget prévisionnel : un plafond, et sa ventilation par poste.
 *
 * Trois choses que cet écran refuse de faire deviner :
 *
 * * **Le total peut dépasser la somme des postes.** Garder une réserve non
 *   ventilée est un choix de gestion, pas une erreur de saisie — l'écart est
 *   donc affiché et nommé « réserve », au lieu d'être signalé comme une faute.
 * * **La somme des postes peut dépasser le total.** Là, en revanche, c'est un
 *   problème : on a promis plus qu'on n'a. L'écran le dit en clair, mais
 *   n'empêche pas d'enregistrer — c'est parfois l'état réel d'un budget qu'on
 *   est en train de refaire.
 * * **Le budget s'écrit d'un bloc.** Il change rarement et toujours pour une
 *   raison ; une suite de retouches dont on ne reconstitue plus l'intention
 *   vaut moins qu'un enregistrement unique, tracé, avec sa note.
 *
 * Le champ « note » n'est pas décoratif : c'est là qu'on écrit *pourquoi* on a
 * relevé le plafond de 500 €, et c'est la seule chose qu'on cherchera trois
 * mois plus tard.
 */
export function BudgetEditor({
  budget,
  categories,
  currency,
  spentByCategory,
  label,
  money,
  busy,
  error,
  onSave,
}: {
  budget: Budget | null
  categories: ExpenseCategory[]
  currency: string
  /** Déjà engagé par poste — pour ne pas planifier sous ce qui est dépensé. */
  spentByCategory: Map<string, number>
  label: (value: I18n) => string
  money: (minor: number) => string
  busy: boolean
  error: string | null
  onSave: (payload: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const toAmount = (minor: number) => (minor / 100).toFixed(2)
  const toMinor = (value: string) => {
    const parsed = Number.parseFloat(value.replace(',', '.'))
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0
  }

  const [total, setTotal] = useState(toAmount(budget?.total_minor ?? 0))
  const [notes, setNotes] = useState(budget?.notes ?? '')
  const [lines, setLines] = useState<Record<string, string>>(() => {
    const start: Record<string, string> = {}
    for (const line of budget?.lines ?? []) start[line.category_id] = toAmount(line.planned_minor)
    return start
  })

  const totalMinor = toMinor(total)
  const plannedMinor = Object.values(lines).reduce((sum, value) => sum + toMinor(value), 0)
  const reserve = totalMinor - plannedMinor

  if (!open) {
    return (
      <Card className="mt-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 className="font-display text-heading-md text-content-primary">
              {t('admin.finance.budgetTitle')}
            </h2>
            <p className="mt-1 text-sm text-content-secondary">
              {budget
                ? t('admin.finance.budgetSetAt', {
                    amount: money(budget.total_minor),
                    at: budget.updated_at
                      ? new Date(budget.updated_at).toLocaleDateString()
                      : '—',
                  })
                : t('admin.finance.budgetUnset')}
            </p>
            {budget?.notes ? (
              <p className="mt-2 text-caption text-content-muted">« {budget.notes} »</p>
            ) : null}
          </div>
          <Button size="sm" variant={budget ? 'outline' : 'ember'} onClick={() => setOpen(true)}>
            {budget ? t('admin.finance.budgetAdjust') : t('admin.finance.budgetDefine')}
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card className="mt-8">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.finance.budgetTitle')}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.finance.budgetHint')}</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block text-caption text-content-muted">
          {t('admin.finance.budgetTotal', { currency })}
          <input
            inputMode="decimal"
            value={total}
            onChange={(event) => setTotal(event.target.value)}
            className={cn(INPUT, 'tabular-nums')}
          />
        </label>
        <label className="block text-caption text-content-muted">
          {t('admin.finance.budgetNote')}
          <input
            value={notes}
            placeholder={t('admin.finance.budgetNotePlaceholder')}
            onChange={(event) => setNotes(event.target.value)}
            className={INPUT}
          />
        </label>
      </div>

      {/* La ventilation. Facultative : un budget global sans détail reste un
          budget, et forcer à tout ventiler ferait inventer des chiffres. */}
      <p className="mt-6 overline text-content-muted">{t('admin.finance.budgetByLine')}</p>
      {categories.length === 0 ? (
        <p className="mt-2 text-sm text-content-muted">{t('admin.finance.noCategories')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {categories.map((category) => {
            const spent = spentByCategory.get(category.id) ?? 0
            const planned = toMinor(lines[category.id] ?? '')
            return (
              <li key={category.id} className="flex flex-wrap items-center gap-3">
                <span className="min-w-40 flex-1 text-sm text-content-primary">
                  {label(category.name_i18n)}
                  {spent > 0 ? (
                    <span
                      className={cn(
                        'ml-2 text-caption',
                        planned > 0 && spent > planned
                          ? 'text-ember-300'
                          : 'text-content-muted',
                      )}
                    >
                      {t('admin.finance.alreadySpent', { amount: money(spent) })}
                    </span>
                  ) : null}
                </span>
                <input
                  inputMode="decimal"
                  aria-label={label(category.name_i18n)}
                  value={lines[category.id] ?? ''}
                  placeholder="0,00"
                  onChange={(event) =>
                    setLines((current) => ({ ...current, [category.id]: event.target.value }))
                  }
                  className={cn(INPUT, 'mt-0 w-32 tabular-nums')}
                />
              </li>
            )
          })}
        </ul>
      )}

      {/* Le rapprochement entre le plafond et la ventilation, en clair. */}
      <div className="mt-5 space-y-1 border-t border-line-subtle pt-4 text-sm">
        <p className="flex justify-between gap-3">
          <span className="text-content-secondary">{t('admin.finance.budgetPlanned')}</span>
          <span className="tabular-nums text-content-primary">{money(plannedMinor)}</span>
        </p>
        <p className="flex justify-between gap-3">
          <span className="text-content-secondary">
            {reserve >= 0 ? t('admin.finance.budgetReserve') : t('admin.finance.budgetOver')}
          </span>
          <span
            className={cn(
              'tabular-nums',
              reserve >= 0 ? 'text-content-primary' : 'text-ember-300',
            )}
          >
            {money(Math.abs(reserve))}
          </span>
        </p>
        {reserve < 0 ? (
          <p className="pt-1 text-caption text-ember-300">
            {t('admin.finance.budgetOverHint')}
          </p>
        ) : null}
      </div>

      {error ? <p className="mt-4 text-sm text-ember-300">{error}</p> : null}

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          disabled={busy}
          onClick={() =>
            onSave({
              total_minor: totalMinor,
              notes: notes.trim() || null,
              // Un poste laissé vide n'est pas un poste à zéro : il n'est pas
              // ventilé du tout, et l'envoyer à zéro afficherait « 0 € prévu »
              // là où il n'y a pas encore de décision.
              lines: categories
                .filter((category) => toMinor(lines[category.id] ?? '') > 0)
                .map((category) => ({
                  category_id: category.id,
                  planned_minor: toMinor(lines[category.id] ?? ''),
                })),
            })
          }
        >
          {t('admin.project.save')}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          {t('admin.project.cancel')}
        </Button>
      </div>
    </Card>
  )
}

const INPUT =
  'mt-1 h-11 w-full rounded-md border border-line bg-bg-elevated px-3 text-sm text-content-primary'
